import { useState } from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users, X, VideoOff, MicOff, Monitor } from "lucide-react";

interface Participant {
  id: string;
  user_id: string;
  role: string;
  is_video_enabled: boolean;
  is_audio_enabled: boolean;
  is_screen_sharing: boolean;
  guest_name?: string | null;
  avatar_url?: string | null;
  profiles?: {
    display_name: string;
    avatar_url: string | null;
  } | null;
}

interface MobileParticipantsDrawerProps {
  participants: Participant[];
  currentUserId: string;
  waitingRoomContent?: React.ReactNode;
}

export const MobileParticipantsDrawer = ({ 
  participants, 
  currentUserId,
  waitingRoomContent
}: MobileParticipantsDrawerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className="relative h-14 w-14 rounded-full border border-border/60 bg-background shadow-lg transition-transform hover:scale-110"
          title="View participants"
        >
          <Users className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-secondary text-secondary-foreground text-xs flex items-center justify-center font-medium">
            {participants.length}
          </span>
        </Button>
      </DrawerTrigger>
      <DrawerContent className="h-[85vh] bg-[linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.18))]">
        <DrawerHeader className="border-b border-border/70 text-left">
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle className="flex items-center text-lg">
                <Users className="h-5 w-5 mr-2" />
                Participants
              </DrawerTitle>
              <DrawerDescription>
                {participants.length} {participants.length === 1 ? 'person' : 'people'} in the meeting
              </DrawerDescription>
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-3 py-4">
            {participants.map((participant) => {
              const displayName = participant.guest_name || participant.profiles?.display_name || "Unknown";
              const avatarUrl = participant.avatar_url || participant.profiles?.avatar_url;
              const isCurrentUser = participant.user_id === currentUserId;
              
              return (
                <div 
                  key={participant.id} 
                  className={`flex items-center space-x-3 rounded-2xl border p-3 transition-colors ${
                    isCurrentUser ? 'border-primary/20 bg-primary/5' : 'border-border/60 bg-card/80'
                  }`}
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarImage src={avatarUrl || undefined} alt={displayName} />
                    <AvatarFallback className="text-sm font-medium">
                      {displayName[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {displayName}
                      {isCurrentUser && <span className="text-muted-foreground text-sm ml-2">(You)</span>}
                    </p>
                    <div className="mt-1 flex space-x-2">
                      {!participant.is_video_enabled && (
                        <VideoOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      {!participant.is_audio_enabled && (
                        <MicOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      {participant.is_screen_sharing && (
                        <Monitor className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-1">
                    {participant.role === "host" && (
                      <Badge variant="outline" className="text-xs">Host</Badge>
                    )}
                    {participant.guest_name && (
                      <Badge variant="outline" className="text-xs">Guest</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        
        {waitingRoomContent && (
          <div className="border-t border-border/70">
            {waitingRoomContent}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
